import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import styles from "./verify.module.scss";
import { useAuthToken } from "../../hooks/useAuthToken";
import Checkbox from "../../components/checkbox/Checkbox";
import Button from "src/components/button/Button";
import NewAuthPageLayout from "@src/components/auth-page-layout/NewAuthPageLayout";
import AuthCode, { AuthCodeRef } from "react-auth-code-input";
import "./customStyle.css";
import ArrowIcon from "src/assets/icons/curvearrow.svg?react";
import moment from "moment";
import { useAnalytics, Events, useToast } from "@hooks";
import {
  getOrCreateSessionId,
  setSessionUserId,
} from "../../utils/sentryTrackingConfig";
import { resetLogoutTracking } from "../../utils/logoutTrackingGuard";
import { fetchMeId } from "../../utils/fetchMeWithToken";
import { apiUrl } from "@constants";
import { logger } from "../../utils/logger";
import { persistIdpV2Tokens, resendIdpV2EmailOtp, verifyIdpV2EmailOtp } from "../../utils/idpV2Auth";

type Props = {};

const VerifyMutation = gql`
  mutation verifyEmailCode($input: VerifyCodeInput!) {
    verifyEmailCode(input: $input)
  }
`;

const ResendMutation = gql`
  mutation resendEmailCode($input: EmailInput!) {
    resendEmailCode(input: $input)
  }
`;

const CODE_EXPIRY_SECONDS = 60 * 10;
const RESEND_COOLDOWN_SECONDS = 60;

function AdminVerify(props: Props) {
  const [mfa_code, setMFACode] = useState("");
  const [authError, setAuthError] = useState("");
  const { setAuthToken, removeAuthToken } = useAuthToken();
  const { push, location } = useHistory();
  const { trackEvent, segment } = useAnalytics();
  const { successToast, errorToast } = useToast();
  const [remainingSeconds, setRemainingSeconds] = useState(CODE_EXPIRY_SECONDS);
  const [disableMFA, setDisableMFA] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [idpLoading, setIdpLoading] = useState(false);
  const [idpResending, setIdpResending] = useState(false);
  const AuthInputRef = useRef<AuthCodeRef>(null);

  const [verify, { loading, error }] = useMutation<{ verifyEmailCode: string }>(
    VerifyMutation,
    {
      onCompleted: async (data) => {
        const token = data.verifyEmailCode;
        const userId = apiUrl ? await fetchMeId(apiUrl, token) : undefined;
        const state = location.state as { email?: string } | undefined;
        const email = state?.email;
        if (email) {
          resetLogoutTracking();
          const sessionId = getOrCreateSessionId();
          const timestamp = new Date().toISOString();
          if (userId) setSessionUserId(userId);
          const d = {
            email,
            loginType: "2FA" as const,
            sessionId,
            timestamp,
            userId,
          };
          if (userId) {
            await segment?.identify(userId, { email });
          }
          trackEvent(Events.actions.logIn, d);
          trackEvent(Events.actions.sessionStarted, d);
        }
        setAuthToken(token);
        push("/");
      },
      onError: (error) => {
        logger.debug(`[Error]`, error);
      },
    },
  );
  const [resend, { loading: resending }] = useMutation(ResendMutation);

  useEffect(() => {
    const timerInterval = setInterval(() => {
      setRemainingSeconds((prevSeconds) => prevSeconds - 1);
    }, 1000);

    if (remainingSeconds <= 0) {
      clearInterval(timerInterval);
    }

    return () => clearInterval(timerInterval);
  }, [remainingSeconds]);

  useEffect(() => {
    if (error?.message) {
      setAuthError(error.message);
    }
  }, [error]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }

    const cooldownTimer = setInterval(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => clearInterval(cooldownTimer);
  }, [resendCooldown]);

  const handleChange = useCallback((value) => {
    setMFACode(value);
  }, []);

  const handleDisableMFA = () => {
    setDisableMFA(!disableMFA);
  };

  const state: any = location.state;

  if (!state) {
    push("/");
    return null;
  } else {
    const handleSubmit = async () => {
      removeAuthToken();
      try {
        if (state.idpChallengeId) {
          setAuthError("");
          setIdpLoading(true);
          const result = await verifyIdpV2EmailOtp(state.idpChallengeId, mfa_code, disableMFA);
          if (!result.access_token) {
            throw new Error("Invalid MFA response");
          }
          persistIdpV2Tokens(result);
          const userId = result.user_id;
          const email = state.email;
          if (email) {
            resetLogoutTracking();
            const sessionId = getOrCreateSessionId();
            const timestamp = new Date().toISOString();
            if (userId) setSessionUserId(userId);
            const d = {
              email,
              loginType: "2FA" as const,
              sessionId,
              timestamp,
              userId,
            };
            if (userId) {
              await segment?.identify(userId, { email });
            }
            trackEvent(Events.actions.logIn, d);
            trackEvent(Events.actions.sessionStarted, d);
          }
          setAuthToken(result.access_token);
          push("/");
          return;
        }

        await verify({
          variables: {
            input: {
              code: mfa_code,
              disable: disableMFA,
              email: state.email,
              clientId: localStorage.getItem("clientId"),
            },
          },
        });
      } catch (e) {
        setAuthError(e instanceof Error ? e.message : "Invalid MFA code");
        logger.info(e);
      } finally {
        setIdpLoading(false);
      }
    };

    const canResend = resendCooldown === 0 && !resending && !idpResending;

    const applyResendSuccess = () => {
      setMFACode("");
      setAuthError("");
      setRemainingSeconds(CODE_EXPIRY_SECONDS);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      AuthInputRef.current?.clear();
      successToast("A new verification code was sent to your email.");
    };

    const resendCode = async () => {
      if (!canResend) {
        return;
      }

      try {
        if (state.idpChallengeId) {
          setIdpResending(true);
          await resendIdpV2EmailOtp(state.idpChallengeId);
          applyResendSuccess();
          return;
        }

        const sent = await resend({
          variables: { input: { email: state.email } },
        });
        if (sent.data?.resendEmailCode !== false) {
          applyResendSuccess();
        } else {
          errorToast("Could not resend the code. Please try again.");
        }
      } catch (e) {
        logger.debug("[Resend error]", e);
        errorToast("Could not resend the code. Please try again.");
      } finally {
        setIdpResending(false);
      }
    };

    const handleLogin = () => {
      push("/login");
    };

    const emailMask = `${state.email.charAt(0)}...@${state.email.split("@")[1]}`;
    const formattedTime = moment.utc(remainingSeconds * 1000).format("mm:ss");

    return (
      <NewAuthPageLayout
        onSubmit={handleSubmit}
        loading={loading || idpLoading}
        title={<div>Email Authentication</div>}
        buttonTitle={"Verify"}
      >
        {authError && (
          <>
            <div className={styles.noteBlock}>
              <div
                className={styles.noteBlock_label}
                dangerouslySetInnerHTML={{ __html: authError }}
              />
              {(authError.startsWith("Your authentication code has expired.") || authError === "MFA challenge expired") && (
                <Button
                  disabled={!canResend}
                  className={styles.resendButton}
                  label={"Resend code"}
                  isLoading={resending || idpResending}
                  onClick={resendCode}
                />
              )}
            </div>
          </>
        )}
        <div className={styles.ssoNotice}>
          <span>Sign In using Single Sign-On to avoid this step!</span>
          <Button
            label="Click to Sign-In with SSO"
            size="small"
            className={styles.loginButton}
            onClick={handleLogin}
          />
        </div>
        <div className={styles.verifyEmail}>
          A verification code was sent to{" "}
          <span className={styles.emailMask}>{emailMask}</span>
          <br />
          Check your email and enter the code below.
        </div>
        <AuthCode
          onChange={handleChange}
          inputClassName={"codeInput"}
          containerClassName="authContainer"
          length={6}
          ref={AuthInputRef}
        />
        {remainingSeconds > 0 && (
          <div className={styles.expireWrapper}>
            <div className={styles.error}>{`Expires In ${formattedTime}`}</div>
          </div>
        )}
        <div className={styles.checkboxWrapper}>
          <Checkbox
            id={"disableMFA"}
            label={
              "Disable Two-Factor-Authentication on this computer for 30 days."
            }
            checked={disableMFA}
            onChange={handleDisableMFA}
            className={styles.flexStart}
          />
        </div>
        <div className={styles.arrowDiv}>
          <span className={styles.arrowText}>
            Need help logging in?
            <br />
            Click to Chat with us!
          </span>
          <ArrowIcon width="40" height="200" />
        </div>
        <Button
          disabled={loading || idpLoading || mfa_code === ""}
          className={styles.verifyButton}
          label={"Verify"}
          isLoading={loading || idpLoading}
          onClick={handleSubmit}
        />
        <div className={styles.back}>
          Didn&apos;t get an email?&nbsp;
          {resending || idpResending ? (
            <span className={styles.resendStatus}>Sending…</span>
          ) : resendCooldown > 0 ? (
            <span className={styles.resendCooldown}>
              Resend in {moment.utc(resendCooldown * 1000).format("m:ss")}
            </span>
          ) : (
            <span
              className={styles.backLink}
              onClick={resendCode}
              role="button"
              tabIndex={0}
            >
              Resend code
            </span>
          )}
        </div>
      </NewAuthPageLayout>
    );
  }
}

export default AdminVerify;
